import { getConfigurationValue, getHost } from '../utils/general';

// NOTE: These are lazy getters instead of module-level const assignments.
// Module-level const calls like `const baseurl = getHost()` can cause
// "Cannot access 'X' before initialization" (TDZ) errors in production
// builds when Rollup/Vite encounters circular module dependencies.
// Using functions defers evaluation until call-time, safely avoiding TDZ.
const getBaseUrl = () => getHost();
const getRsaPublicKeyVer = () =>
  getConfigurationValue('RSA_PUBLIC_KEY_VERSION');

export const B2C_ONE_ACCOUNT_PATH = '/b2b/user/account/b2c';
export const B2B_ACCOUNT_PATH = '/b2b/user/account';
export const B2B_ACCOUNT_VIEW_PATH = '/b2b/account/view';

export const IMAGES_ASSETS_URL = 'https://assets.thdstatic.com/images/v1';

const showNewPurchaseHistoryPage = () =>
  getConfigurationValue('ENABLE_NEW_PURCHASE_HISTORY_PAGE') === '1';

// B2C paths:
export const B2C_ACCOUNT_PATH_WITH_MEMBER_ID = (memberId: string) =>
  `${B2C_ONE_ACCOUNT_PATH}/${memberId}`;
export const B2C_ACCOUNT_PAYMENT_PATH_WITH_MEMEBER_ID = (memberId: string) =>
  `${B2C_ONE_ACCOUNT_PATH}/${memberId}`;
export const B2C_ADDRESSES_PATH = (memberId: string) =>
  `${B2C_ACCOUNT_PATH_WITH_MEMBER_ID(memberId)}/addresses`;
export const B2C_PAYMENTS_PATH = (memberId: string) =>
  `${B2C_ACCOUNT_PAYMENT_PATH_WITH_MEMEBER_ID(memberId)}/paymentcards`;
const B2C_PAYPAL_BASE_PATH = (memberId: string) =>
  `${B2C_ACCOUNT_PAYMENT_PATH_WITH_MEMEBER_ID(memberId)}/paypal`;
const B2C_PAYPAL_V2_BASE_PATH = (memberId: string) =>
  `${B2C_ACCOUNT_PAYMENT_PATH_WITH_MEMEBER_ID(memberId)}/v2/paypal`;
export const B2C_PAYPAL_INIT_PATH = (memberId: string) =>
  `${B2C_PAYPAL_BASE_PATH(memberId)}/initpaypal`;
export const B2C_PAYPAL_SAVE_PATH = (memberId: string) =>
  `${B2C_PAYPAL_BASE_PATH(memberId)}/verifysavepaypal`;
export const B2C_PAYPAL_V2_INIT_PATH = (memberId: string) =>
  `${B2C_PAYPAL_V2_BASE_PATH(memberId)}/initpaypal`;
export const B2C_PAYPAL_V2_SAVE_PATH = (memberId: string) =>
  `${B2C_PAYPAL_V2_BASE_PATH(memberId)}/verifysavepaypal`;
export const B2C_ACCOUNT_PROFILE_PATH = (memberId: string) =>
  `${B2C_ACCOUNT_PATH_WITH_MEMBER_ID(memberId)}/profile?checkPhoneEligibility=true`;

// B2B paths:
export const B2B_ACCOUNT_PATH_WITH_USER_ID = (userId: string) =>
  `${B2B_ACCOUNT_PATH}/${userId}`;
export const B2B_ACCOUNT_PATH_WITH_USER_ID_SVOC_ID = (
  userId: string,
  svocId: string,
) => `${B2B_ACCOUNT_PATH_WITH_USER_ID(userId)}/customer/${svocId}`;
export const B2B_ADDRESSES_PATH = (userId: string, svocId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID_SVOC_ID(userId, svocId)}/addresses`;
export const B2B_UPDATE_ADDRESSES_PATH = (userId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID(userId)}/addresses`;
export const B2B_PAYMENTS_PATH = (userId: string, svocId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID_SVOC_ID(userId, svocId)}/payment`;
export const B2B_PAYMENTS_PATH_WITH_USER_ID = (userId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID(userId)}/payment`;
export const B2B_PAYMENTS_RETRIEVE_PATH = (userId: string, svocId: string) =>
  `${B2B_PAYMENTS_PATH(userId, svocId)}/retrieve`;
export const B2B_PRO_HOUSE_ACCOUNT_RETRIEVE_PATH = (
  userId: string,
  svocId: string,
) => `${B2B_PAYMENTS_PATH(userId, svocId)}/houseAccount/retrieve`;

export const B2B_PRO_HOUSE_ACCOUNT_WITH_USER_ID_SVOC_ID_PATH = (
  userId: string,
  svocId: string,
) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID_SVOC_ID(userId, svocId)}/payment/houseAccount`;

// Mock PHA account Enrollment/Unenrollment
export const B2B_PRO_HOUSE_ACCOUNT_T2C_ENROLL_PATH = (userId: string) =>
  `${B2B_PAYMENTS_PATH_WITH_USER_ID(userId)}/houseAccount/t2c`;
export const B2B_PRO_HOUSE_ACCOUNT_T2C_UNENROLL_PATH = (userId: string) =>
  `${B2B_PAYMENTS_PATH_WITH_USER_ID(userId)}/houseAccount/removet2c`;

export const B2B_UPDATE_PAYMENT_PATH = (userId: string) =>
  `${B2B_PAYMENTS_PATH_WITH_USER_ID(userId)}/update`;
export const B2B_UPDATE_STATUS_PATH = (userId: string) =>
  `${B2B_PAYMENTS_PATH_WITH_USER_ID(userId)}/updatestatus`;
export const B2B_ADD_UPDATE_NICKNAME_PATH = (userId: string) =>
  `${B2B_PAYMENTS_PATH_WITH_USER_ID(userId)}/addOrUpdateCardNickName`;
export const B2B_BILLING_ADDRESS_PATH = (userId: string, svocId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID_SVOC_ID(userId, svocId)}/billingAddress`;
export const B2B_PAYPAL_SET_CHECKOUT_PATH = (userId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID(userId)}/paypal/setExpressCheckout`;
export const B2B_PAYPAL_SAVE_PATH = (userId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID(userId)}/paypal/savePayPalAccount`;
export const B2B_PAYPAL_V2_SET_CHECKOUT_PATH = (userId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID(userId)}/v2/paypal/initPayPal`;
export const B2B_PAYPAL_V2_SAVE_PATH = (userId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID(userId)}/v2/paypal/savePayPalAccount`;
export const B2B_STOP_ALL_AUTHORIZATION = (userId: string, svocId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID_SVOC_ID(userId, svocId)}/payment/creditcard/stopPaa`;
export const B2B_ACCOUNT_PROFILE_PATH = (userId: string, svocId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID_SVOC_ID(userId, svocId)}/profile`;
export const B2B_ACCOUNT_CNP_ENROLLMENT = (userId: string, svocId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID_SVOC_ID(userId, svocId)}/payment/creditcard/CNPEnrollmentRequest`;
export const B2B_ACCOUNT_HD_WALLET_UNENROLLMENT = (
  userId: string,
  svocId: string,
) => `${B2B_PAYMENTS_PATH(userId, svocId)}/hdWallet/unenroll`;
export const B2B_GET_CITI_CARDS = (
  userId: string,
  svocId: string,
  citiAccountId: string,
) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID_SVOC_ID(userId, svocId)}/citi/${citiAccountId}/payment/retrieve`;
export const B2B_GIFT_CARD_BALANCE_PATH = (userId: string, svocId: string) =>
  `${B2B_PAYMENTS_PATH(userId, svocId)}/getGiftCardBalance`;
export const B2B_ACCOUNT_RESEND_T2C_PATH = (userId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID(userId)}/payment/creditcard/resendt2c`;
export const B2B_ACCOUNT_CONTACT_NUMBER_PATH = (userId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID(userId)}/custInfo/contactNumber`;

// Resend t2c invites for both PHA and CC. Resending invites for both single and multiple contacts
export const B2B_ACCOUNT_MULTI_RESEND_T2C_PATH = (userId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID(userId)}/payment/t2c/multiResend`;

export const B2B_PAYMENTS_PATH_WITH_USER_ID_SVOC_ID = (
  userId: string,
  svocId: string,
) => `${B2B_ACCOUNT_PATH_WITH_USER_ID_SVOC_ID(userId, svocId)}/payment`;
export const B2B_PAYMENTS_PATH_WITH_USER_ID_V2 = (userId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID(userId)}/v2/payment`;

// B2B pages:
// Uses fs flag to render purchase history experience if active
export const B2B_PURCHASE_HISTORY_PAGE = () =>
  showNewPurchaseHistoryPage()
    ? `/myaccount/purchase-history`
    : `${B2B_ACCOUNT_VIEW_PATH}/order/summary`;

// Fetches a list of users like the Add Manage Users UI page on a B2B account
export const B2B_MANAGE_USERS_PATH = (userId: string, svocId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID_SVOC_ID(userId, svocId)}/manageuser`;

// Common paths:
export const ZIP_CODE_PATH = `${B2B_ACCOUNT_PATH}/zipCode`;
export const OG_UPDATE_ADDRESS_QA_URL =
  'https://staging.v2.ordergroove.com/customer/update_shipping_default';
export const OG_UPDATE_ADDRESS_PROD_URL =
  'https://api.ordergroove.com/customer/update_shipping_default';
export const PROFILE_PAGE_PATH = '/myaccount/profile';
export const B2B_ACCOUNT_HD_WALLET_ENROLLMENT = (
  userId: string,
  svocId: string,
) =>
  `${B2B_PAYMENTS_PATH_WITH_USER_ID_SVOC_ID(userId, svocId)}/hdWallet/enroll`;

// Link for Home Depot Credit Cards page
export const CREDIT_CARDS_PAGE_URL = '/myaccount/thdcreditcard';

// Links for Home Depot Gift Cards page
export const CASHSTART_RELOAD_GIFT_CARD_PAGE_URL =
  'https://homedepot.cashstar.com/reload/?ref=THD1';
export const B2B_PURCHASE_GIFT_CARD_PATH =
  'https://www.homedepot.com/c/Gift_Cards';
export const RELOAD_GIFT_CARD_PAGE_URL = '/gift-cards/balance';

// PayPal cancel and redirect links for B2C
export const PAYPAL_CANCEL_REDIRECT_PATH = () =>
  `${getBaseUrl()}/myaccount/payments`;
export const PAYPAL_RETURN_PATH = () => `${getBaseUrl()}/myaccount/payments`;

export const PRO_XTRA_PERKS_PATH = () =>
  `${getBaseUrl()}/b2b/account/view/proXtraPerks`;

// Pro Allowance Page
export const MANAGE_PRO_ALLOWANCE_PATH = '/myaccount/proallowance';

// OrangePay
export const GET_ORANGE_PAY_TOKEN = (svocId: string, userId: string) =>
  `/b2b/user/account/${svocId}/orangepay/${userId}/token`;
export const ORANGE_PAY_URL_STG =
  'https://orangepay-ecommerce.payments-stg.homedepot.com/';
export const ORANGE_PAY_URL_PROD =
  'https://orangepay-ecommerce.hdpayments.homedepot.com/';
export const ADD_CREDIT_CARD = (userId: string, svocId: string) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID_SVOC_ID(userId, svocId)}/v2/payment/addPaymentCard`;

// How to authorize payments for Text2Confirm page
export const AUTHORIZE_CARDS_T2C_PAGE_URL = '/c/authorize-cards-text2confirm';

// RSA Encryption
export const GET_RSA_PUBLIC_KEY_URL = () =>
  `https://assets.thdstatic.com/account/address-payments/rsa/${getRsaPublicKeyVer()}/public_key.txt`;
export const GET_RSA_PUBLIC_KEY_QA_URL = () =>
  `https://assets.thdstatic.com/account/address-payments/rsa-qa/${getRsaPublicKeyVer()}/public_key.txt`;
export const B2B_ADD_CHECKING_ACCOUNT_ENCRYPTED = (
  userId: string,
  svocId: string,
) =>
  `${B2B_ACCOUNT_PATH_WITH_USER_ID_SVOC_ID(userId, svocId)}/v2/payment/addCheckingAccount`;
